import { track } from "@/lib/analytics"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getMatterContents, startReading } from "../api"
import { shortDate } from "../answer/format"
import type { MatterFile } from "../contract"
import { docName } from "../matter/proceedings"

const COPY = {
  notice: { what: "notice", go: "Analyse this notice", reading: "notice_no_reply" as const },
  order: { what: "order", go: "Read this order for an appeal", reading: "order_preview" as const },
}

/** The words for a refused start: another notice already read, no order in the document, or a limit. */
export function readRefusal(err: unknown, kind: "notice" | "order"): string {
  const e = err as { status?: number; message?: string } | null
  if (e?.status === 409) {
    if (/another/i.test(e.message ?? "")) return "This workspace already has a full reading of a different notice. Open the workspace to read it, or start a new workspace for this notice."
    return kind === "order" ? "Nira found no order in that document. Choose another, or add the order to the workspace." : "Nira couldn't read that document as a notice. Choose another."
  }
  if (e?.status === 402) return "This reading is part of Nira Pro."
  return `Couldn't start the ${kind === "order" ? "reading" : "analysis"}. Try again.`
}

/**
 * Notice analysis or Appeal or not on a document the workspace already holds: /ask?workspace=…&door=…&doc=….
 * The chosen document goes to the backend with the reading, so it reads that one and not the latest.
 */
export function WorkspaceRead({ caseId, doc, kind, onAsk }: { caseId: string; doc: string; kind: "notice" | "order"; onAsk: () => void }) {
  const navigate = useNavigate()
  const copy = COPY[kind]
  const [file, setFile] = useState<MatterFile | null>(null)
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState("")
  useEffect(() => {
    let live = true
    getMatterContents(caseId).then((c) => { if (live) setFile(c.files.find((f) => f.doc_id === doc) ?? null) }).catch(() => undefined)
    return () => { live = false }
  }, [caseId, doc])
  const go = async () => {
    setBusy(true)
    setSaid("")
    track("reading_started", { kind: kind === "order" ? "appeal" : "notice", from: "workspace" })
    try {
      const reading = await startReading(caseId, copy.reading, { doc })
      navigate(`/case/${encodeURIComponent(caseId)}/reading/${encodeURIComponent(reading.id)}`)
    } catch (err) {
      setBusy(false)
      setSaid(readRefusal(err, kind))
    }
  }
  return (
    <div className="nl-sz">
      <div className="nl-lbox nh-drop" data-busy={busy || undefined}>
        <p className="nh-drop-t">{file ? docName(file) : `The ${copy.what} from your workspace`}</p>
        <p className="nh-drop-s">{file?.date ? shortDate(file.date) : "Nira reads this document, not the latest one."}</p>
        <button type="button" className="nh-drop-b" disabled={busy} onClick={() => void go()}>{busy ? "Starting…" : copy.go}</button>
      </div>
      <div className="nl-help"><button type="button" className="nh-drop-ask" onClick={onAsk}>Ask a question instead</button></div>
      <p className="nl-note show" role="status">{said}</p>
    </div>
  )
}
